export interface CoursePoint {
  x: number;
  z: number;
}

export interface CourseSpawn extends CoursePoint {
  /** vehicleDriving의 heading 규약: 0은 +X, 양수는 -Z 방향 회전입니다. */
  heading: number;
}

export interface CourseObstacleCollider {
  /** 물리 판정 반경(m). 렌더 베이스 크기와 같은 단일 진실 원천입니다. */
  radius: number;
  /** 법선 방향 반발 계수(0~1). */
  restitution: number;
  /** 충돌 뒤 접선 속도를 얼마나 유지할지 나타내는 계수(0~1). */
  tangentRetention: number;
}

export interface CourseObstacle extends CoursePoint {
  id: string;
  type: 'cone';
  height: number;
  collider: CourseObstacleCollider;
}

/** 렌더·노면 물리·미니맵이 공유하는 코스 중심선입니다. */
export interface CourseRoute {
  points: readonly CoursePoint[];
  closed: boolean;
  roadHalfWidth: number;
  runoffWidth: number;
  obstacles?: readonly CourseObstacle[];
}

function catmullRom(a: number, b: number, c: number, d: number, t: number): number {
  const t2 = t * t;
  const t3 = t2 * t;
  return 0.5 * (
    2 * b
    + (-a + c) * t
    + (2 * a - 5 * b + 4 * c - d) * t2
    + (-a + 3 * b - 3 * c + d) * t3
  );
}

/**
 * 적은 수의 설계 제어점을 부드러운 중심선으로 바꿉니다.
 * 결과가 canonical route라서 Three.js의 별도 curve 구현과 물리 판정이 어긋나지 않습니다.
 */
export function sampleCatmullRomRoute(
  controls: readonly CoursePoint[],
  closed: boolean,
  samplesPerSegment = 14
): readonly CoursePoint[] {
  if (controls.length < 2) {
    return [...controls];
  }
  const points: CoursePoint[] = [];
  const segmentCount = closed ? controls.length : controls.length - 1;
  const at = (index: number): CoursePoint => {
    if (closed) {
      return controls[(index + controls.length) % controls.length];
    }
    return controls[Math.min(controls.length - 1, Math.max(0, index))];
  };

  for (let segment = 0; segment < segmentCount; segment += 1) {
    const p0 = at(segment - 1);
    const p1 = at(segment);
    const p2 = at(segment + 1);
    const p3 = at(segment + 2);
    for (let sample = 0; sample < samplesPerSegment; sample += 1) {
      const t = sample / samplesPerSegment;
      points.push({
        x: catmullRom(p0.x, p1.x, p2.x, p3.x, t),
        z: catmullRom(p0.z, p1.z, p2.z, p3.z, t),
      });
    }
  }
  if (!closed) {
    points.push({ ...controls[controls.length - 1] });
  }
  return points;
}

export function createCircularRoute(
  centerX: number,
  centerZ: number,
  radiusX: number,
  radiusZ: number,
  segments = 192,
  clockwise = true
): readonly CoursePoint[] {
  return Array.from({ length: segments }, (_, index) => {
    const angle = (index / segments) * Math.PI * 2;
    return {
      x: centerX + Math.cos(angle) * radiusX,
      z: centerZ + Math.sin(angle) * radiusZ * (clockwise ? -1 : 1),
    };
  });
}

export function createFigureEightRoute(
  centerX: number,
  centerZ: number,
  radiusX: number,
  radiusZ: number,
  segments = 240
): readonly CoursePoint[] {
  return Array.from({ length: segments }, (_, index) => {
    const angle = (index / segments) * Math.PI * 2;
    return {
      x: centerX + Math.sin(angle) * radiusX,
      z: centerZ + Math.sin(angle * 2) * radiusZ,
    };
  });
}

/** 평면상의 점과 중심선 사이 최단거리(m). */
export function distanceToCourseRoute(route: CourseRoute, x: number, z: number): number {
  const points = route.points;
  if (points.length === 0) {
    return Number.POSITIVE_INFINITY;
  }
  if (points.length === 1) {
    return Math.hypot(x - points[0].x, z - points[0].z);
  }
  let minimumSquared = Number.POSITIVE_INFINITY;
  const segmentCount = route.closed ? points.length : points.length - 1;
  for (let index = 0; index < segmentCount; index += 1) {
    const start = points[index];
    const end = points[(index + 1) % points.length];
    const dx = end.x - start.x;
    const dz = end.z - start.z;
    const lengthSquared = dx * dx + dz * dz;
    const t = lengthSquared <= 1e-9
      ? 0
      : Math.min(1, Math.max(0, ((x - start.x) * dx + (z - start.z) * dz) / lengthSquared));
    const nearestX = start.x + dx * t;
    const nearestZ = start.z + dz * t;
    const distanceSquared = (x - nearestX) ** 2 + (z - nearestZ) ** 2;
    minimumSquared = Math.min(minimumSquared, distanceSquared);
  }
  return Math.sqrt(minimumSquared);
}

export function isOnCourseRoad(route: CourseRoute | undefined, x: number, z: number): boolean {
  return route !== undefined && distanceToCourseRoute(route, x, z) <= route.roadHalfWidth;
}

/** 두 월드 점 사이 진행 방향을 vehicle heading으로 바꿉니다. */
export function headingBetween(start: CoursePoint, end: CoursePoint): number {
  return Math.atan2(-(end.z - start.z), end.x - start.x);
}
